import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supplierOffersAPI, suppliersAPI, SupplierOffer, Supplier, OfferDocument, DocumentType } from '../../services/api';
import DocumentUpload, { PendingDocumentUpload } from './DocumentUpload';

interface SupplierOfferFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  offer?: SupplierOffer | null;
}

const SupplierOfferForm: React.FC<SupplierOfferFormProps> = ({ isOpen, onClose, onSuccess, offer }) => {
  const [formData, setFormData] = useState({
    supplierId: '',
    productName: '',
    description: '',
    quantity: '',
    unitPrice: '',
    totalPrice: '',
    currency: 'USD',
    validFrom: '',
    validUntil: '',
    status: 'PENDING',
    notes: '',
  });
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [documents, setDocuments] = useState<OfferDocument[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocumentUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchSuppliers();
      if (offer) {
        setFormData({
          supplierId: offer.supplierId.toString(),
          productName: offer.productName,
          description: offer.description || '',
          quantity: offer.quantity.toString(),
          unitPrice: offer.unitPrice.toString(),
          totalPrice: offer.totalPrice.toString(),
          currency: offer.currency,
          validFrom: offer.validFrom ? offer.validFrom.split('T')[0] : '',
          validUntil: offer.validUntil ? offer.validUntil.split('T')[0] : '',
          status: offer.status,
          notes: offer.notes || '',
        });
        fetchDocuments(offer.id);
      } else {
        setFormData({
          supplierId: '',
          productName: '',
          description: '',
          quantity: '',
          unitPrice: '',
          totalPrice: '',
          currency: 'USD',
          validFrom: '',
          validUntil: '',
          status: 'PENDING',
          notes: '',
        });
        setDocuments([]);
        setPendingDocuments([]);
      }
      setError('');
    }
  }, [isOpen, offer]);

  const fetchSuppliers = async () => {
    try {
      const data = await suppliersAPI.getAll({});
      setSuppliers(data.data || data);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
    }
  };

  const fetchDocuments = async (offerId: number) => {
    try {
      const docs = await supplierOffersAPI.getDocuments(offerId);
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  };

  const handleUploadDocument = async (
    file: File,
    fileType: DocumentType,
    fileName: string,
  ) => {
    if (offer) {
      // If editing existing offer, upload immediately
      try {
        await supplierOffersAPI.uploadDocument(offer.id, file, fileType, fileName);
        await fetchDocuments(offer.id);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to upload document');
        throw err;
      }
    } else {
      // If creating new offer, store documents to upload after creation
      setPendingDocuments([
        ...pendingDocuments,
        {
          clientId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          fileType,
          fileName,
        },
      ]);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      await supplierOffersAPI.deleteDocument(documentId);
      if (offer) {
        await fetchDocuments(offer.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete document');
      throw err;
    }
  };

  const handleDeletePendingDocument = (clientId: string) => {
    setPendingDocuments(pendingDocuments.filter((document) => document.clientId !== clientId));
  };

  const calculateTotalPrice = () => {
    const quantity = parseFloat(formData.quantity) || 0;
    const unitPrice = parseFloat(formData.unitPrice) || 0;
    const total = quantity * unitPrice;
    setFormData({ ...formData, totalPrice: total.toFixed(2) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload: any = {
        supplierId: parseInt(formData.supplierId),
        productName: formData.productName,
        quantity: parseInt(formData.quantity),
        unitPrice: parseFloat(formData.unitPrice),
        totalPrice: parseFloat(formData.totalPrice),
        currency: formData.currency,
        status: formData.status,
      };

      if (formData.description) payload.description = formData.description;
      if (formData.validFrom) payload.validFrom = new Date(formData.validFrom).toISOString();
      if (formData.validUntil) payload.validUntil = new Date(formData.validUntil).toISOString();
      if (formData.notes) payload.notes = formData.notes;

      let createdOfferId: number | undefined;

      if (offer) {
        await supplierOffersAPI.update(offer.id, payload);
      } else {
        const createdOffer = await supplierOffersAPI.create(payload);
        createdOfferId = createdOffer.id;

        // Upload pending documents if any
        if (pendingDocuments.length > 0) {
          for (const doc of pendingDocuments) {
            try {
              await supplierOffersAPI.uploadDocument(createdOfferId, doc.file, doc.fileType, doc.fileName);
            } catch (err) {
              console.error('Failed to upload document:', err);
              // Continue uploading other documents even if one fails
            }
          }
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save supplier offer');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-medium text-neutral-900">
            {offer ? 'Edit Supplier Offer' : 'Add Supplier Offer'}
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700">Supplier *</label>
              <select
                required
                value={formData.supplierId}
                onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              >
                <option value="">Select Supplier</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700">Product Name *</label>
              <input
                type="text"
                required
                value={formData.productName}
                onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700">Quantity *</label>
              <input
                type="number"
                required
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                onBlur={calculateTotalPrice}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700">Unit Price *</label>
              <input
                type="number"
                step="0.01"
                required
                value={formData.unitPrice}
                onChange={(e) => setFormData({ ...formData, unitPrice: e.target.value })}
                onBlur={calculateTotalPrice}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700">Total Price *</label>
              <input
                type="number"
                step="0.01"
                required
                value={formData.totalPrice}
                onChange={(e) => setFormData({ ...formData, totalPrice: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700">Currency *</label>
              <select
                required
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700">Valid From</label>
              <input
                type="date"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700">Valid Until</label>
              <input
                type="date"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">Status *</label>
            <select
              required
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
            >
              <option value="PENDING">Pending</option>
              <option value="ACCEPTED">Accepted</option>
              <option value="REJECTED">Rejected</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
            />
          </div>

          {/* Documents Section - Available for both create and edit */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-neutral-900 mb-3">Documents</h4>
            <DocumentUpload
              documents={documents}
              pendingDocuments={pendingDocuments}
              onUpload={handleUploadDocument}
              onDelete={handleDeleteDocument}
              onDeletePending={handleDeletePendingDocument}
              disabled={loading}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : offer ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierOfferForm;
